import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import { useCallback, useMemo } from 'react';

import { type InputComponent } from '..';

export interface InputQuantityProps extends InputComponent {
  min?: number;
  max?: number;
  step?: number;
}

export function InputQuantity({
  className,
  disabled,
  hidden,
  hint,
  label,
  nameOverride,
  readOnly,
  required,
  step = 1,
  min = -1 * Number.MAX_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
}: InputQuantityProps) {
  const { errors, values, setFieldValue, setFieldTouched } =
    useFormikContext<Record<string, number>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const quantity: number = Number(get(values, name)) || 0;

  // This is necessary to support non integer steps.
  // 1. Modulo is not valid for floating points.
  // 2. If we are not doing integer math then we need to round so we don't
  //    have weird looking numbers. 1.499999999999 is 1.5 if step is 0.1
  const stepsDecimalPlaces = useMemo(
    () => step?.toString()?.split('.')?.[1]?.length || 0,
    [step],
  );

  const changeValue = useCallback(
    (type: 'INCREMENT' | 'DECREMENT') => {
      if (disabled) return;

      const multiplier = type === 'INCREMENT' ? 1 : -1;
      const updated = Math.max(
        min,
        Math.min(max, quantity + step * multiplier),
      );

      setFieldTouched(name, true);
      // The quantity value is rounded to the stepsDecimalPlaces so we don't have goofy looking numbers
      setFieldValue(
        name,
        parseFloat(updated.toFixed(stepsDecimalPlaces)),
        true,
      );
    },
    [
      disabled,
      quantity,
      min,
      max,
      step,
      stepsDecimalPlaces,
      setFieldTouched,
      setFieldValue,
      name,
    ],
  );

  const handleQuantityInputChange = useCallback(
    (value: string) => {
      const parsedValue = value.replace(/[^\d.]/g, '') || 0;

      setFieldTouched(name, true, false);
      setFieldValue(name, parsedValue, true);
    },
    [setFieldTouched, setFieldValue, name],
  );

  const currentValue = Number(get(values, name)) || 0;

  return (
    <FormControl size="small" fullWidth className={className}>
      <InputLabel>{label}</InputLabel>
      <OutlinedInput
        sx={{ minWidth: 120, '& input': { textAlign: 'center' } }}
        value={currentValue}
        onChange={(e) => handleQuantityInputChange(e.target.value)}
        disabled={disabled}
        hidden={hidden}
        readOnly={readOnly}
        fullWidth
        required={required && !readOnly}
        label={label}
        name={name}
        type="number"
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              onClick={() => changeValue('INCREMENT')}
              edge="end"
              disabled={disabled || readOnly}
            >
              <Add />
            </IconButton>
          </InputAdornment>
        }
        startAdornment={
          <InputAdornment position="start">
            <IconButton
              onClick={() => changeValue('DECREMENT')}
              edge="start"
              disabled={disabled || readOnly}
            >
              <Remove />
            </IconButton>
          </InputAdornment>
        }
      />
      {(errors[name] || hint) && (
        <FormHelperText error={!!errors[name]}>
          {errors[name] || hint}
        </FormHelperText>
      )}
    </FormControl>
  );
}
